import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useToast } from '@chakra-ui/react'
import Toast from '../../../../../components/Toast'
import { useAcceptPortfolioManagementAgreementMutation } from '../../../../../redux/services/coreApi'
import { setUser } from '../../../../../redux/slices/sessionSlice'
import type { RootState } from '../../../../../redux/store'
import PortfolioManagementAgreementModal from './PortfolioManagementAgreementModal'

interface PortfolioManagementAgreementGateProps {
  children: React.ReactNode
}

const PortfolioManagementAgreementGate: React.FC<
  PortfolioManagementAgreementGateProps
> = ({ children }) => {
  const user = useSelector((state: RootState) => state.session.user)
  const dispatch = useDispatch()
  const toast = useToast()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [acceptAgreement, { isLoading }] =
    useAcceptPortfolioManagementAgreementMutation()

  const hasAccepted = user?.hasAcceptedPortfolioManagementAgreement

  const handleAccept = async () => {
    try {
      const updatedUser = await acceptAgreement(null).unwrap()
      dispatch(setUser({ user: updatedUser }))
      setIsModalOpen(false)
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="success">Portfolio Management Agreement accepted.</Toast>
        ),
      })
    } catch {
      toast({
        position: 'top-right',
        render: () => (
          <Toast type="error">Something went wrong. Please try again.</Toast>
        ),
      })
    }
  }

  if (hasAccepted) {
    return <>{children}</>
  }

  return (
    <>
      <PortfolioManagementAgreementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAccept={handleAccept}
        isLoading={isLoading}
      />

      <div className="relative">
        <div className="pointer-events-none select-none blur-sm opacity-40">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="card p-8 max-w-md w-full text-center shadow-xl">
            <h3 className="text-lg font-semibold mb-2">Agreement required</h3>
            <p className="text-description mb-6">
              To access portfolio deposits and performance data, you must accept
              the Portfolio Management Agreement.
            </p>
            <button
              className="button w-full"
              onClick={() => setIsModalOpen(true)}
            >
              Review &amp; Accept Agreement
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default PortfolioManagementAgreementGate
